import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  ArrowRightCircle, ExternalLink, StickyNote, Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreBadge } from './ScoreBadge';
import {
  type ColumnDef,
  SortableHeader,
  filterItems,
  renderColumnFilter,
  sortItems,
  usePersistedColumnFilters,
  usePersistedColumnVisibility,
} from './tenderTableUtils';
import { TenderTableToolbar } from './TenderTableToolbar';
import { type SavedTender, tenderScore } from './types';

const FILTER_STORAGE_KEY = 'atek-saved-tenders-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-saved-tenders-column-visibility';
const PAGE_SIZE = 50;

interface SavedTendersTableProps {
  items: SavedTender[];
  isLoading: boolean;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onRowClick: (s: SavedTender) => void;
  onEditNotes: (s: SavedTender) => void;
  onConvert: (s: SavedTender) => void;
  onDelete: (s: SavedTender) => void;
  convertedTenders: Map<string, { opportunityId: string; companyName: string }>;
  isConvertPending: boolean;
}

export function SavedTendersTable({
  items,
  isLoading,
  isRefreshing,
  onRefresh,
  onRowClick,
  onEditNotes,
  onConvert,
  onDelete,
  convertedTenders,
  isConvertPending,
}: SavedTendersTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('score');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [page, setPage] = useState(0);

  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of items) if (s.tender.source) set.add(s.tender.source);
    return Array.from(set).sort().map((v) => ({ label: v, value: v }));
  }, [items]);

  const columns: ColumnDef<SavedTender>[] = useMemo(() => ([
    {
      key: 'score',
      label: 'Score',
      sortKey: 'score',
      filterType: 'number-range',
      getValue: (s) => tenderScore(s.tender),
      render: (s) => <ScoreBadge score={tenderScore(s.tender)} />,
      headerClassName: 'w-[90px]',
    },
    {
      key: 'name',
      label: 'Title',
      sortKey: 'name',
      filterType: 'text',
      getValue: (s) => s.tender.name,
      render: (s) => (
        <div className="max-w-sm">
          <p className="font-medium text-xs break-words">{s.tender.name}</p>
          {s.tender.summary && <p className="text-xs text-muted-foreground break-words mt-0.5">{s.tender.summary}</p>}
          {s.tenderId && <p className="text-[10px] text-muted-foreground/50 font-mono mt-0.5">{s.tenderId}</p>}
        </div>
      ),
    },
    {
      key: 'organisationName',
      label: 'Organisation',
      sortKey: 'organisationName',
      filterType: 'text',
      getValue: (s) => s.tender.organisationName ?? '',
      render: (s) => <span className="text-sm">{s.tender.organisationName ?? '—'}</span>,
    },
    {
      key: 'source',
      label: 'Source',
      filterType: 'select',
      filterOptions: sourceOptions,
      getValue: (s) => s.tender.source,
      render: (s) => <Badge variant="outline" className="text-xs">{s.tender.source}</Badge>,
    },
    {
      key: 'publishDate',
      label: 'Published',
      sortKey: 'publishDate',
      filterType: 'date-range',
      getValue: (s) => s.tender.publishDate ?? '',
      render: (s) => <span className="text-sm text-muted-foreground whitespace-nowrap">{s.tender.publishDate ?? '—'}</span>,
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortKey: 'deadline',
      filterType: 'date-range',
      getValue: (s) => s.tender.deadline ?? '',
      render: (s) => <span className="text-sm text-muted-foreground whitespace-nowrap">{s.tender.deadline ?? '—'}</span>,
    },
    {
      key: 'notes',
      label: 'Notes',
      filterType: 'text',
      getValue: (s) => s.notes ?? '',
      render: (s) => s.notes
        ? <p className="text-xs text-yellow-700 max-w-[180px] truncate" title={s.notes}>{s.notes}</p>
        : <span className="text-muted-foreground/30 text-xs">—</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      noFilter: true,
      align: 'right',
      getValue: () => '',
      headerClassName: 'w-[140px] text-right',
      render: (s) => {
        const converted = convertedTenders.get(s.id);
        return (
          <div className="flex items-center justify-end gap-0.5">
            <button
              className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Edit notes"
              onClick={(e) => { e.stopPropagation(); onEditNotes(s); }}
            >
              <StickyNote className="h-3.5 w-3.5" />
            </button>
            {converted ? (
              <a
                href={`/crm/opportunities/${converted.opportunityId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded text-orange-600 hover:text-orange-800 transition-colors"
                title={`Voir dans opportunité — ${converted.companyName}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              <button
                className="p-1 rounded text-green-600 hover:text-green-800 transition-colors disabled:opacity-40"
                title="Move to Opportunity"
                disabled={isConvertPending}
                onClick={(e) => { e.stopPropagation(); onConvert(s); }}
              >
                <ArrowRightCircle className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              className="p-1 rounded text-muted-foreground hover:text-red-600 transition-colors"
              title="Remove from saved"
              onClick={(e) => { e.stopPropagation(); onDelete(s); }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <a
              href={s.tender.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 p-1"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        );
      },
    },
  ]), [sourceOptions, convertedTenders, isConvertPending, onEditNotes, onConvert, onDelete]);

  const {
    columnFilters, setColumnFilter, clearColumnFilters, hasActiveColumnFilters,
  } = usePersistedColumnFilters(FILTER_STORAGE_KEY);

  const { toggleColumn, isVisible } = usePersistedColumnVisibility(VISIBILITY_STORAGE_KEY, columns);

  const visibleColumns = useMemo(() => columns.filter((c) => c.noFilter || isVisible(c.key)), [columns, isVisible]);

  const filteredItems = useMemo(() => filterItems(items, columns, columnFilters), [items, columns, columnFilters]);
  const sortedItems = useMemo(() => sortItems(filteredItems, columns, sortKey, sortDirection), [filteredItems, columns, sortKey, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () => sortedItems.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [sortedItems, currentPage],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDirection(-1); }
    setPage(0);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <TenderTableToolbar
        items={sortedItems}
        columns={columns}
        csvFilename="saved-tenders"
        hasActiveColumnFilters={hasActiveColumnFilters}
        onClearColumnFilters={clearColumnFilters}
        isVisible={isVisible}
        toggleColumn={toggleColumn}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        countLabel={`Showing ${sortedItems.length} of ${items.length}`}
      />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
            </TableRow>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right', col.headerClassName)}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      align={col.align}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
            {!isLoading && pagedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  {items.length === 0
                    ? 'No saved tenders yet. Click the bookmark icon on any tender to save it here.'
                    : 'No saved tenders match the filters'}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && pagedItems.map((s) => (
              <TableRow
                key={s.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(s)}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm align-top',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.className,
                    )}
                  >
                    {col.render ? col.render(s) : (String(col.getValue(s) ?? '') || '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
